import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction, Checkbox } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import axios from 'axios';

const ManageAiInstructions = () => {
    const [instructions, setInstructions] = useState([]);
    const [selectedInstructions, setSelectedInstructions] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [newInstructionName, setNewInstructionName] = useState('');
    const [newInstructionContent, setNewInstructionContent] = useState('');
    const [instructionToDelete, setInstructionToDelete] = useState(null);

    useEffect(() => {
        fetchInstructions();
    }, []);

    const fetchInstructions = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/instructions`);
            setInstructions(response.data);
        } catch (error) {
            console.error('Error fetching instructions:', error);
        }
    };

    const handleAddInstruction = async () => {
        if (!newInstructionName.trim() || !newInstructionContent.trim()) {
            alert("Name and content are required.");
            return;
        }

        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/api/instructions`, {
                name: newInstructionName,
                content: newInstructionContent
            });
            fetchInstructions();
            setNewInstructionName('');
            setNewInstructionContent('');
            setDialogOpen(false);
        } catch (error) {
            console.error('Error adding instruction:', error);
        }
    };

    const handleDeleteInstruction = async () => {
        try {
            if (instructionToDelete) {
                await axios.delete(`${process.env.REACT_APP_API_URL}/api/instructions/${instructionToDelete}`);
            } else {
                for (const id of selectedInstructions) {
                    await axios.delete(`${process.env.REACT_APP_API_URL}/api/instructions/${id}`);
                }
            }
            fetchInstructions();
            setSelectedInstructions([]);
            setInstructionToDelete(null);
            setDeleteDialogOpen(false);
        } catch (error) {
            console.error('Error deleting instruction:', error);
        }
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const instructions = JSON.parse(e.target.result);
                    for (const instruction of instructions) {
                        await axios.post(`${process.env.REACT_APP_API_URL}/api/instructions`, instruction);
                    }
                    fetchInstructions();
                } catch (error) {
                    console.error('Error uploading instructions:', error);
                }
            };
            reader.readAsText(file);
        }
    };

    const handleSelectInstruction = (id) => {
        setSelectedInstructions(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleDeleteIconClick = (id) => {
        setInstructionToDelete(id);
        setDeleteDialogOpen(true);
    };

    return (
        <Box>
            <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setDialogOpen(true)}
                sx={{ marginRight: 2 }} // Add right margin
            >
                Add Instruction
            </Button>
            <input
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                id="upload-file"
            />
            <label htmlFor="upload-file">
                <Button variant="contained" component="span" startIcon={<UploadFileIcon />}>
                    Upload Instructions
                </Button>
            </label>
            <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                disabled={selectedInstructions.length === 0}
                sx={{ marginLeft: 2 }}
            >
                Delete Selected
            </Button>
            <List>
                {instructions.map((instruction) => (
                    <ListItem key={instruction.id}>
                        <Checkbox
                            checked={selectedInstructions.includes(instruction.id)}
                            onChange={() => handleSelectInstruction(instruction.id)}
                            tabIndex={-1}
                            disableRipple
                        />
                        <ListItemText primary={instruction.name} secondary={instruction.content} />
                        <ListItemSecondaryAction>
                            <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteIconClick(instruction.id)}>
                                <DeleteIcon />
                            </IconButton>
                        </ListItemSecondaryAction>
                    </ListItem>
                ))}
            </List>
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>Add New AI Instruction</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Please enter the name and content for the new AI instruction.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Instruction Name"
                        fullWidth
                        value={newInstructionName}
                        onChange={(e) => setNewInstructionName(e.target.value)}
                    />
                    <TextField
                        margin="dense"
                        label="Instruction Content"
                        fullWidth
                        multiline
                        rows={4}
                        value={newInstructionContent}
                        onChange={(e) => setNewInstructionContent(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleAddInstruction} color="primary">
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete AI Instructions</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the selected instructions? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteInstruction} color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ManageAiInstructions;
